import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, User } from '../services/auth.service';
import { clearMirandaStorage } from '@/lib/miranda-storage';
import { checkSession, sessionClearedElsewhere } from '@/lib/auth-session';
import { clearReturnTo, currentPath, saveReturnTo } from '@/lib/auth-return-to';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /**
   * Hi ha sessió desada però no s'ha pogut comprovar (sense xarxa, deploy en marxa…).
   * No s'ha esborrat res: `checkAuth()` ho torna a provar.
   */
  connectionError: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  updateUser: (data: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);

  // Verificar autenticación al cargar
  useEffect(() => {
    checkAuth();
  }, []);

  // Sense connexió en obrir l'app (mode avió, cobertura): quan torna la xarxa es reintenta sol.
  useEffect(() => {
    if (!connectionError) return;
    const retry = () => {
      checkAuth();
    };
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [connectionError]);

  // Una altra pestanya ha tancat la sessió (o li ha caducat): aquesta no s'ha de quedar amb
  // la interfície oberta i sense tokens. `storage` només arriba a les pestanyes que no han
  // fet el canvi. No es pot saber si allà ha estat un logout o una caducitat: es tracta com
  // un logout i NO es desa cap ruta de retorn, perquè al PC del mostrador qui entri després
  // no aterri a la pantalla de qui hi era. `ProtectedRoute` porta a `/login`.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.storageArea !== localStorage || !sessionClearedElsewhere(event)) return;
      clearReturnTo();
      setUser(null);
      setConnectionError(false);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const checkAuth = async () => {
    setIsLoading(true);
    try {
      // Els tokens només s'esborren amb un 401 de `/auth/verify` (ho fa `checkSession`).
      const result = await checkSession(() => authService.verify());
      switch (result.status) {
        case 'authenticated':
          setUser(result.user);
          setConnectionError(false);
          break;
        case 'unavailable':
          // Ni 401 ni resposta bona: la sessió es conserva tal com era. Qui ja era dins
          // segueix dins; en arrencar, `ProtectedRoute` ensenya «no s'ha pogut connectar».
          console.warn('Auth check unavailable: session kept');
          setConnectionError(true);
          break;
        case 'rejected':
          // Sessió caducada: es desa on s'era per tornar-hi després d'entrar.
          saveReturnTo(currentPath());
          setUser(null);
          setConnectionError(false);
          break;
        default:
          setUser(null);
          setConnectionError(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login({ email, password });

      // Guardar tokens
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);

      // Guardar usuario
      setUser(response.user);
      setConnectionError(false);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await authService.logout(refreshToken);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Limpiar estado local sin importar el resultado
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      // La conversa de Miranda queda desada al navegador (F1): al PC del mostrador, el
      // següent que entri no ha de trobar-se la conversa de qui hi era (revisió M1).
      clearMirandaStorage();
      // Ídem la ruta on tornar: només val per a una sessió caducada, no per a un tancament.
      clearReturnTo();
      setUser(null);
      setConnectionError(false);
    }
  };

  const updateUser = (data: Partial<User>) => {
    setUser((prev) => prev ? { ...prev, ...data } : prev);
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    connectionError,
    login,
    logout,
    checkAuth,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
